import { DispatcherEvent } from '@carvana/merch-native';
import LoadingTile from '@carvana/merch-tile/components/LoadingTile';
import { MfeLoad } from '@carvana/microfrontend';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { getMfeCacheBuster } from '@/common/MicroFrontEnds';
import { staticCdnUrl } from '~/constants/environment';
import ConditionalTile from '~/constants/srp/ConditionalTile';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useAppliedFilters } from '~/context/common/AppliedFiltersContext';
import { useSendMessageToNative } from '~/context/common/NativeContext';
import StaticContext, {
  useEventCorrelationId
} from '~/context/common/StaticContext';
import { useShowComposerTreatment } from '~/context/composer/ComposerContext';
import {
  useInventoryVehicles,
  useRequestIds,
  useTotalMatchedInventory,
  useUserDeliveryInfo
} from '~/context/srp/InventoryContext';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import { generateErrorLogger } from '~/utilities/loggers';
import styles from './SebastianTileWrapper.module.css';
import ObservableTile from '../ObservableTile';

const SEBS_MODULE = 'SebsTileModule';

type OwnProps = {
  name: ConditionalTile.SebastianTileA | ConditionalTile.SebastianTileB;
  position: number;
};

function SebastianTileWrapper({ name, position }: OwnProps) {
  const trackEvent = useTrackEvent();
  const ref = useRef(null);

  const { browserCookieId } = useContext(StaticContext);
  const correlationId = useEventCorrelationId();
  const filters = useAppliedFilters();
  const vehicles = useInventoryVehicles();
  const totalMatchedInventory = useTotalMatchedInventory();
  const deliveryInfo = useUserDeliveryInfo();
  const { searchRequestId } = useRequestIds();
  const sendMessageToNative = useSendMessageToNative<DispatcherEvent>();
  const showComposerTreatment = useShowComposerTreatment();

  const onNativeQuestionClicked = useCallback(
    (payload: Record<string, unknown>, onError?: (error: Error) => void) => {
      sendMessageToNative(DispatcherEvent.SebastianPrompt, payload, {
        onError
      });
    },
    [sendMessageToNative]
  );

  useEffect(() => {
    MfeLoad({
      files: [
        [
          SEBS_MODULE,
          [
            {
              component: 'default',
              default: {
                file: 'main.js',
                manifest: `${staticCdnUrl}/sebs-question-card/assets-manifest.json?v=${getMfeCacheBuster()}`
              }
            }
          ]
        ]
      ]
    }).catch(generateErrorLogger('SebastianTile - MfeLoad'));
  }, []);

  if (showComposerTreatment) {
    return null;
  }

  return (
    <ObservableTile
      observeRef={ref}
      name={name}
      onVisible={() => {
        trackEvent('Conditional tile', {
          action: 'viewed',
          tile: name,
          position
        });
      }}
    >
      <div className={styles.wrapper} id={name}>
        <MfeReactComponent
          module={SEBS_MODULE}
          component="default"
          bcid={browserCookieId}
          cardPosition={position}
          correlationId={correlationId}
          filters={filters}
          onNativeQuestionClicked={onNativeQuestionClicked}
          searchRequestId={searchRequestId}
          searchResults={{ totalMatchedInventory, vehicles }}
          zip5={deliveryInfo?.zip5 ?? ''}
          loading={<LoadingTile />}
        />
      </div>
    </ObservableTile>
  );
}

export default withErrorBoundary(SebastianTileWrapper, {
  section: 'SRP SebastianTile'
});
